import React, { useState, useEffect, useRef } from 'react'

import { Contact } from 'types'
import { SettingsObject } from 'pages/Admin/Settings'
import { portalService } from 'api/services/portal'
import { productService } from 'api/services/product'
import { SettingsType } from 'api/types/portal'
import { ProductCategory, ProductType } from 'api/types/product'
import { getCookie } from 'cookies-next'

export type AppContextType = {
  showAccessMode: boolean
  setShowAccessMode: (show: boolean) => void
  categories: ProductCategory[]
  setCategories: (categories: ProductCategory[]) => void
  products: ProductType[]
  setProducts: (products: ProductType[]) => void
  settings: SettingsObject | null
  contacts: Contact[] | null
  setContacts: (contacts: Contact[] | null) => void
  backOfficeMode: boolean
  setBackOfficeMode: (mode: boolean) => void
  getProductsAndCategories?: () => Promise<void>
  isCallingApi: boolean
  setIsCallingApi: (mode: boolean) => void
}

export const AppContext = React.createContext<AppContextType>({
  showAccessMode: false,
  setShowAccessMode: () => {},
  categories: [],
  setCategories: () => {},
  products: [],
  setProducts: () => {},
  settings: null,
  contacts: [],
  setContacts: () => {},
  backOfficeMode: false,
  setBackOfficeMode: () => {},
  getProductsAndCategories: async () => {},
  isCallingApi: false,
  setIsCallingApi: () => {},
})

export const AppProvider = ({ children }: { children: any }) => {
  const [loadedData, _setLoadedData] = useState<boolean>(false)
  const [showAccessMode, setShowAccessMode] = useState(false) // State for modal
  const [settings, setSettings] = useState<SettingsObject | null>(null)
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [products, setProducts] = useState<ProductType[]>([])
  const [contacts, setContacts] = useState<Contact[] | null>([])
  const [backOfficeMode, setBackOfficeMode] = useState<boolean>(false)
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false)

  const loadedRef = useRef<boolean>(loadedData)

  // keep ref in sync with state
  useEffect(() => {
    loadedRef.current = loadedData
  }, [loadedData])

  const getSettings = async () => {
    const response = (await portalService.getConfigList()) as SettingsType[]

    const newSettings: SettingsObject = response
      .map(item => ({
        code: item.code.trim(),
        value: item.value,
      }))
      .reduce((acc, curr) => {
        acc[curr.code as keyof SettingsObject] = curr.value
        return acc
      }, {} as SettingsObject)

    setSettings(newSettings)
  }

  const getProductsAndCategories = async () => {
    try {
      const cats = (await productService.getCategories()) as any[]
      const prods = (await productService.getPortalProductList()) as
        | ProductType[]
        | any

      setCategories(cats || [])
      setProducts(prods || [])
    } catch (error) {
      // keep defaults if fetch fails
      console.warn('Failed to load products/categories', error)
    }
  }

  // Run once on mount. getPageSettings is stable (empty deps) and guards itself
  useEffect(() => {
    getSettings()
    if (getCookie('accessToken')) {
      getProductsAndCategories()
    }
  }, [])

  return (
    <AppContext.Provider
      value={{
        showAccessMode,
        setShowAccessMode,
        settings,
        categories,
        setCategories,
        products,
        setProducts,
        contacts,
        setContacts,
        backOfficeMode,
        setBackOfficeMode,
        getProductsAndCategories,
        isCallingApi,
        setIsCallingApi,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}
